// slices/clientSlice.ts
import { createSlice } from "@reduxjs/toolkit";
import {
    createUserCompany,
    createUserJobs,
    updateUserJob,
    deleteUserJob,
    createUserNotifications,
    createUserPrivacies,
    currentUserVerificationData,
    getTypeCategories,
    getUserCompanies,
    getUserNotifications,
    getUserPreferences,
    getUserPrivacies,
    updateUserCompanies,
    updateUserPreferences,
    validateVATNumber,
    getClientStats,
} from "../Actions/ClientActions";
import { ClientState } from "../../modules/user/models";
import { createGeneralSetting, UpdateGeneralSettings } from "../Actions/ProfessionalActions";

const initialState: ClientState = {
    userPreferences: null,
    company: null,
    verificationData: null,
    notificationSettings: null,
    vatValidation: null,
    typeCategories: [],
    categoriesByType: {}, // Initialize empty cache
    error: null,
    userPrivacy: null,
    clientStats: null,
};

const clientSlice = createSlice({
    name: "client",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(getUserPreferences.fulfilled, (state, action) => {
                state.userPreferences = action.payload;
                state.error = null;
            })
            .addCase(getUserPreferences.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch preferences";
            })
            .addCase(updateUserPreferences.fulfilled, (state, action) => {
                state.userPreferences = action.payload;
                state.error = null;
            })
            .addCase(updateUserCompanies.fulfilled, (state, action) => {
                state.company = action.payload;
                state.error = null;
            })

            .addCase(getUserCompanies.fulfilled, (state, action) => {
                state.company = action.payload;
                state.error = null;
            })
            .addCase(currentUserVerificationData.fulfilled, (state, action) => {
                state.verificationData = action.payload;
                state.error = null;
            })

            .addCase(getUserNotifications.fulfilled, (state, action) => {
                state.notificationSettings = action.payload;
            })
            .addCase(createUserCompany.fulfilled, (state, action) => {
                state.company = action.payload;
            })
            .addCase(createUserNotifications.fulfilled, (state, action) => {
                state.notificationSettings = action.payload;
            })
            .addCase(validateVATNumber.fulfilled, (state, action) => {
                state.vatValidation = action.payload;
            })
            .addCase(validateVATNumber.rejected, (state, action) => {
                state.vatValidation = null;
                state.error = typeof action.payload === "string" ? action.payload : "VAT validation failed";
            })
            .addCase(getUserPrivacies.fulfilled, (state, action) => {
                state.userPrivacy = action.payload;
            })
            .addCase(getTypeCategories.fulfilled, (state, action) => {
                // Store in the general typeCategories array (for backward compatibility)
                state.typeCategories = action.payload || [];
                
                // Cache by type if we have the type information
                if (action.payload && action.payload.length > 0 && action.meta.arg) {
                    const typeId = action.meta.arg.id;
                    state.categoriesByType[typeId] = action.payload;
                }
            })
            .addCase(createUserPrivacies.fulfilled, (state, action) => {
                state.userPrivacy = action.payload;
            })
            .addCase(createUserJobs.fulfilled, (state, action) => {
                // Handle successful job creation
                state.error = null;
            })
            .addCase(updateUserJob.fulfilled, (state, action) => {
                // Handle successful job update
                state.error = null;
            })
            .addCase(deleteUserJob.fulfilled, (state, action) => {
                // Handle successful job deletion
                state.error = null;
            })
            .addCase(createUserJobs.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Failed to create job";
            })

            .addCase(currentUserVerificationData.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch verification data";
            })
            .addCase(getClientStats.fulfilled, (state, action) => {
                state.clientStats = action.payload;
                state.error = null;
            })
            .addCase(getClientStats.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch client stats";
            });
    },
});

export default clientSlice.reducer;
